import { Box, Button, Paper, Stack, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';

const HEADER_BG = '#1e40af';
const HEADER_BORDER = '#60A5FA';
const ACCENT = 'rgb(219, 234, 254)';

export default function TermsAndConditions() {
  const navigate = useNavigate();

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc' }}>
      <Box
        sx={{
          height: 72,
          px: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          bgcolor: HEADER_BG,
          color: 'white',
          borderBottom: `1px solid ${HEADER_BORDER}`,
        }}
      >
        <Stack direction="row" spacing={1.25} alignItems="center">
          <img src="/ssiconfull.png" alt="SimSuite logo" style={{ width: 160 }} />
        </Stack>

        <Button
          variant="outlined"
          onClick={() => navigate('/')}
          sx={{
            color: 'white',
            borderColor: 'rgba(255,255,255,0.65)',
            textTransform: 'none',
            '&:hover': {
              borderColor: 'white',
              bgcolor: 'rgba(255,255,255,0.08)',
            },
          }}
        >
          Back to home
        </Button>
      </Box>

      <Box sx={{ maxWidth: 980, mx: 'auto', px: 2, py: 4 }}>
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2.5, md: 4 },
            borderRadius: 3,
            border: '1px solid #e5e7eb',
            bgcolor: 'white',
          }}
        >
          <Typography variant="h4" sx={{ fontWeight: 800, mb: 1 }}>
            Terms and Conditions
          </Typography>

          <Typography sx={{ color: 'text.secondary', mb: 3 }}>
            Last updated: 28/04/2026
          </Typography>

          <Section title="1. About these Terms">
            These Terms and Conditions govern your use of the SimSuite platform,
            accessible via simsuitemed.com and any associated applications.
            By accessing or using SimSuite, you agree to be bound by these Terms.
            If you do not agree, you must not use the platform.
          </Section>

          <Section title="2. About SimSuite">
            SimSuite is a medical simulation platform designed for education,
            training, and scenario-based learning. It allows users to create,
            manage, and participate in simulated clinical scenarios, including
            electronic patient record-style environments.
          </Section>

          <Section title="3. Eligibility and accounts">
            You must be at least 18 years old to use SimSuite. You are responsible
            for maintaining the confidentiality of your account details and for all
            activity carried out under your account. We reserve the right to suspend
            or terminate accounts that breach these Terms.
          </Section>

          <Section title="4. Educational use only">
            SimSuite is provided solely for educational, training, and simulation
            purposes. The platform does not provide medical advice, diagnosis, or
            treatment. It must not be used to make clinical decisions or to guide
            the care of any real patient.
          </Section>

          <Section title="5. Simulated content">
            All scenarios, patient records, observations, investigations,
            medications, documentation, and other clinical material within SimSuite
            are intended to be fictional or simulated. Any resemblance to real
            patients, events, or clinical cases should be avoided by users when
            creating content.
          </Section>

          <Section title="6. Prohibition of real patient information">
            Users must not upload, enter, transmit, or otherwise make available any
            information that identifies, or could reasonably be used to identify, a
            real individual.
            <br />
            <br />
            This includes, but is not limited to, names, dates of birth, NHS
            numbers, hospital numbers, addresses, contact details, photographs,
            clinical images, investigation results, documents, or any other
            patient-identifiable information.
            <br />
            <br />
            All data entered into SimSuite must be fictional, anonymised, or
            simulated for training purposes only. Users are responsible for ensuring
            compliance with applicable data protection laws and professional
            confidentiality obligations, including UK GDPR.
          </Section>

          <Section title="7. Acceptable use">
            You agree not to use SimSuite for any unlawful purpose, attempt to gain
            unauthorised access to systems or data, interfere with the operation of
            the platform, upload harmful or inappropriate content, or reverse
            engineer, copy, or reproduce any part of the platform without permission.
          </Section>

          <Section title="8. User content">
            You retain ownership of content you create within SimSuite. By using
            the platform, you grant us a non-exclusive, worldwide licence to store,
            process, display, and use that content solely for the purpose of
            operating, maintaining, and improving the platform.
            <br />
            <br />
            We are not responsible for user-generated content and may remove content
            that we reasonably believe breaches these Terms.
          </Section>

          <Section title="9. Platform availability">
            We aim to keep SimSuite available and functioning, but we do not
            guarantee uninterrupted or error-free access. We may modify, suspend, or
            discontinue any part of the platform at any time.
          </Section>

          <Section title="10. Fees and payments">
            Some features of SimSuite may be offered on a paid basis. Where fees
            apply, prices and payment terms will be stated at the point of purchase.
            Access to paid features may be suspended or withdrawn if payment is not
            received.
          </Section>

          <Section title="11. Intellectual property">
            All intellectual property rights in SimSuite, including software,
            design, branding, layout, text, graphics, features, and functionality,
            are owned by or licensed to SimSuite, except for user-generated content.
            You may not copy, reproduce, distribute, modify, or create derivative
            works from any part of the platform without prior written permission.
          </Section>

          <Section title="12. Third-party content and licences">
            SimSuite may include or link to third-party content, tools, services, or
            resources. We are not responsible for third-party content or services.
            Where third-party materials are used under licence, relevant attribution
            or credit will be provided where required.
          </Section>

          <Section title="13. Limitation of liability">
            To the fullest extent permitted by law, SimSuite shall not be liable for
            any loss, damage, or claim arising out of or in connection with use of,
            or inability to use, the platform; reliance on simulated content,
            scenarios, or outputs; or application of knowledge or skills derived
            from the platform in a real-world clinical or non-clinical setting.
            <br />
            <br />
            SimSuite is provided on an “as is” and “as available” basis without
            warranties of any kind, whether express or implied.
            <br />
            <br />
            Nothing in these Terms excludes or limits liability for death or
            personal injury caused by negligence, fraud, or any other liability
            which cannot be excluded or limited under applicable law.
            <br />
            <br />
            In all other cases, our total liability to any user shall not exceed the
            amount paid by that user, if any, for access to SimSuite in the preceding
            12 months.
          </Section>

          <Section title="14. Termination">
            We may suspend or terminate your access to SimSuite if you breach these
            Terms or misuse the platform. You may stop using SimSuite at any time.
          </Section>

          <Section title="15. Changes to these Terms">
            We may update these Terms from time to time. Continued use of SimSuite
            after changes are made will be treated as acceptance of the updated
            Terms.
          </Section>

          <Section title="16. Governing law">
            These Terms are governed by the laws of England and Wales. Any disputes
            shall be subject to the exclusive jurisdiction of the courts of England
            and Wales.
          </Section>

          <Section title="17. Contact">
            If you have any questions about these Terms, please contact:
            <br />
            <br />
            contact@simsuitemed.com
          </Section>

          <Box
            sx={{
              mt: 4,
              p: 2,
              borderRadius: 2,
              bgcolor: ACCENT,
              border: `1px solid ${HEADER_BORDER}`,
            }}
          >
            <Typography sx={{ fontSize: 14, color: '#1e3a8a' }}>
              © 2026 SimSuite. All rights reserved.
            </Typography>
          </Box>
        </Paper>
      </Box>
    </Box>
  );
}

function Section({ title, children }) {
  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
        {title}
      </Typography>
      <Typography sx={{ lineHeight: 1.75, color: '#374151' }}>
        {children}
      </Typography>
    </Box>
  );
}